import React from "react";

import _ from "lodash";

import { InfoCardWebsite } from "@similarweb/ui-components";

import { i18nFilter } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import { prettifyCategoryFilter } from "app/filters/prettifyCategoryFilter";
import { addSiteToWatchList } from "app/pages/workspace/common/actions_creators/common_worksapce_action_creators";
import { isDomainInWorkspace } from "app/pages/workspace/common/workspacesUtils";
import { AssetsService } from "app/services/AssetsService";
import { fetchSiteInfoQuery } from "app/services/sitesService/queries/sitesQueries";
import { getSiteDataFromResponse } from "app/services/sitesService/sitesUtils";
import { SwRedux } from "app/services/SwRedux";
import { hasInvestorPermission } from "app/services/Workspaces.service";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { TrackButton } from "../../TrackButton/TrackButton";

import { DomainInfoContainer, DomainInfoButton } from "./StyledComponents";
import { allTrackers } from "app/services/track/track";

interface IWebsiteTooltipContentProps {
    domain: string;
    mainDomainOnly?: boolean;
    reposition?: () => void;
    hideTrackButton?: boolean;
    handleOpenQualificationSideBar?: (e: FixmeType) => void;
}

interface IWebsiteTooltipContentState {
    data: any;
    showing: string | null;
    isTracked: boolean;
    isTrackLoading: boolean;
}

class WebsiteTooltipContentClass extends React.Component<IWebsiteTooltipContentProps, IWebsiteTooltipContentState> {
    public static defaultProps = { domain: null };

    private categoryIconFilter: FixmeType;
    private swNavigator: FixmeType;
    private mounted = false;
    private loading = false;
    private hasTrackWorkspaces = false;

    constructor(props: IWebsiteTooltipContentProps) {
        super(props);
        this.swNavigator = proNavigator;
        this.state = {
            data: null,
            showing: props.domain,
            isTracked: false,
            isTrackLoading: true,
        };
    }

    public fetchSiteInfo(domain: string, mainDomainOnly?: boolean) {
        // don't fetch if still loading
        if (!domain || this.loading) {
            return;
        }
        this.loading = true;
        const { isWWW, country, webSource = "Total" } = this.swNavigator.getParams();
        void fetchSiteInfoQuery({
            keys: domain,
            webSource,
            country: country ? country : undefined,
            mainDomainOnly: mainDomainOnly ?? isWWW !== "*",
        }).then((data) => {
            // verify the correct request
            if (this.state.showing === domain && this.mounted) {
                this.loading = false;
                // set state and reposition
                const siteData = getSiteDataFromResponse(data, domain);
                // @ts-ignore FIXME: strict migration - reposition is optional but legacy code assumes callers always pass it
                this.setState({ data: siteData }, () => this.props.reposition());
            }
        });
    }

    public async componentDidMount() {
        this.fetchSiteInfo(this.props.domain, this.props.mainDomainOnly);
        this.mounted = true;
        isDomainInWorkspace(this.props.domain)
            .then(({ hasLists, isTracked }) => {
                this.hasTrackWorkspaces = hasLists;
                if (this.mounted) {
                    // @ts-ignore FIXME: strict migration - isDomainInWorkspace returns isTracked as boolean | undefined; state.isTracked typed boolean
                    this.setState({ isTracked, isTrackLoading: false });
                }
            })
            .catch(() => {
                if (this.mounted) {
                    this.setState({ isTrackLoading: false });
                }
            });
    }

    public componentWillUnmount() {
        this.setState({ data: null, showing: null });
        this.mounted = false;
    }

    public componentDidUpdate(prevProps: IWebsiteTooltipContentProps) {
        if (this.props.domain !== prevProps.domain) {
            this.fetchSiteInfo(this.props.domain, this.props.mainDomainOnly);
        }
    }

    public getLoadingElements() {
        return (
            <div
                className="popup-content--loading"
                dangerouslySetInnerHTML={{ __html: i18nFilter()("website.tooltip.loading") }}
            />
        );
    }

    public onClickTrackButton = async () => {
        SwRedux.getInstance().dispatch(addSiteToWatchList(this.props.domain));
        allTrackers.dispatchTrackEvent("Button", "click", `Track/${this.state.data.displayName || ""}`);
    };

    public getImageSrc = (data: FixmeType) => {
        if (data.category === "Adult") {
            return AssetsService.assetUrl("/images/new-adult-large.png");
        } else {
            return data.image || AssetsService.assetUrl("/images/new-no-image.png");
        }
    };
    // might be moved outside of this component for reusability if neeeded
    public getDomainInfoButton = () => {
        return (
            <DomainInfoContainer>
                <DomainInfoButton
                    type="primary"
                    iconName="show-details"
                    className="qualification-sidebar-toggle"
                    onClick={(e) => {
                        allTrackers.dispatchTrackEvent(
                            "Expanded Side Bar",
                            "open",
                            `open/hover_card/${this.props?.domain || ""}`,
                        );
                        this.props.handleOpenQualificationSideBar?.(e);
                    }}
                >
                    <I18n className="qualification-sidebar-toggle">
                        {"qualification.sidebar.domain.info.button.label"}
                    </I18n>
                </DomainInfoButton>
            </DomainInfoContainer>
        );
    };
    public getTrackOrDomainInfoButton = () => {
        if (this.props.handleOpenQualificationSideBar) {
            return this.getDomainInfoButton();
        }
        return hasInvestorPermission() && !this.props.hideTrackButton ? (
            <TrackButton
                isTracked={this.state.isTracked}
                isLoading={this.state.isTrackLoading}
                isDisabled={this.state.isTrackLoading || this.state.isTracked}
                onClick={this.onClickTrackButton}
                text={this.state.isTracked ? "wa.ao.header.tracked" : "wa.ao.header.track"}
            />
        ) : null;
    };

    public getTrackingForWebsite = () => {
        allTrackers.dispatchTrackEvent("External Link", "click", `${this.state.data.displayName || ""}`);
    };

    public getSiteInfoElement(data: FixmeType) {
        const { webSource = "Total" } = this.swNavigator.getParams();
        data = data || {};
        const isLoadingData = _.isEmpty(data);
        if (isLoadingData) {
            return <InfoCardWebsite isLoadingData={true} />;
        } else {
            return (
                <InfoCardWebsite
                    width={this.props?.handleOpenQualificationSideBar ? 460 : 394}
                    isLoadingData={false}
                    Button={this.getTrackOrDomainInfoButton()}
                    icon={data.icon ? data.icon : "/images/autocomplete-default.png"}
                    imgSrc={this.getImageSrc(data)}
                    // @ts-ignore FIXME: strict migration - prettifyCategoryFilter returns string | null; InfoCardWebsite category expects string | undefined
                    category={prettifyCategoryFilter()(data.category)}
                    websiteName={data.displayName}
                    countryId={data.highestTrafficCountry.id}
                    description={
                        data.description === "" ? i18nFilter()("infotip.description.undefined") : data.description
                    }
                    globalRankingLabel={i18nFilter()("infotip.global")}
                    globalRanking={data.globalRanking === 0 ? "-" : abbrNumberFilter()(data.globalRanking)}
                    highestTrafficCountryRankingLabel={i18nFilter()("infotip.country")}
                    highestTrafficCountryRanking={
                        data.highestTrafficCountryRanking === 0
                            ? "-"
                            : abbrNumberFilter()(data.highestTrafficCountryRanking)
                    }
                    foundedLabel={i18nFilter()("infotip.website.founded")}
                    foundedValue={data.yearFounded}
                    monthlyVisits={data.monthlyVisits === 0 ? "-" : abbrNumberFilter()(data.monthlyVisits)}
                    monthlyVisitsLabel={i18nFilter()("infotip.monthlyVisits")}
                    employees={data.employeeRange}
                    countryOfVisits={data.country}
                    platform={
                        webSource === "Desktop" ? "desktop" : webSource === "MobileWeb" ? "mobile-web" : "combined"
                    }
                    websiteLinkTracking={this.getTrackingForWebsite}
                />
            );
        }
    }

    public render() {
        return (
            <div className="swWebsiteInfoTip">
                <div className="swWebsiteInfoTip-inner">{this.getSiteInfoElement(this.state.data)}</div>
            </div>
        );
    }
}
export const WebsiteTooltipContent = WebsiteTooltipContentClass;
